import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/shared/i18n';
import { useQuery } from '@tanstack/react-query';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
} from '@monksflow/monks-ui';
import { Check, Link2, SquarePen, Wand2 } from 'lucide-react';
import {
  canBindList,
  DEFAULT_DATE_MASK,
  TAXONOMY_DATE_MASK_OPTIONS,
  TAXONOMY_FIELD_TYPE_LABELS,
  TAXONOMY_LEVEL_OPTIONS,
  TAXONOMY_SELECTABLE_FIELD_TYPES,
  taxonomyDateMaskExample,
  type TaxonomyField,
  type TaxonomyFieldGroup,
  type TaxonomyFieldType,
} from '@/entities/taxonomy';
import { taxonomyListsQueryOptions, type TaxonomyListSummary } from '@/entities/taxonomy-list';
import { categoryIconFor, taxonomyCategoriesQueryOptions } from '@/entities/taxonomy-category';
import { taxonomyFieldTemplatesQueryOptions } from '@/entities/taxonomy-field-template';
import { FormField, SectionHeading } from '@/shared/ui/FormDialog';
import { FormSheet } from '@/shared/ui/FormSheet';
import {
  CATEGORY_ICON,
  FIELD_ICON,
  STANDARD_FIELD_CATEGORIES,
  standardFieldLevel,
} from '../FieldLibraryModal/standard-fields';
import { findStandardList, standardFieldListRef } from '../FieldLibraryModal/standard-field-lists';
import { ListBindingFields } from '../ListBinding/ListBindingFields';
import { useListBinding } from '../ListBinding/useListBinding';
import { buildCatalog, CUSTOM, type PickerField } from './buildCatalog';

export interface AddFieldModalProps {
  open: boolean;
  /** The section the add was triggered from — preselects the group for custom fields. */
  defaultGroup: TaxonomyFieldGroup;
  /** Current schema fields — used to mark library entries already added. */
  existingFields: TaxonomyField[];
  /** Hide the Section toggle — subfields inherit their parent's group. */
  lockGroup?: boolean;
  /** Adding a subfield (adjusts the copy). */
  isSubfield?: boolean;
  /** Skip the library picker and open straight to the (custom) details form. */
  skipPicker?: boolean;
  onClose: () => void;
  onAdd: (field: TaxonomyField) => void;
  /** The schema is shared across the organization, so only organization lists can be bound. */
  orgWide?: boolean;
}

// eslint-disable-next-line i18next/no-literal-string -- category identifier matched by name
const SPECIAL = 'Special';

/**
 * One pickable field card. Shared by the category grids and the Custom
 * section so an org-library field looks identical to a shipped one — the
 * distinction that matters to the user is what the field IS, not where the
 * definition happens to live.
 */
function LibraryFieldCard({
  field,
  added,
  summary,
  onClick,
}: {
  field: PickerField;
  added: boolean;
  summary: string;
  onClick: () => void;
}) {
  const { t } = useTranslation('knowledge');
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- both icon maps are indexed by a key they need not contain
  const Icon = FIELD_ICON[field.key] ?? CATEGORY_ICON[field.category] ?? SquarePen;
  return (
    <button
      type="button"
      disabled={added}
      onClick={onClick}
      className={`flex flex-col items-start gap-1.5 rounded-lg border border-border-light bg-card p-3 text-left transition-colors ${
        added ? 'opacity-60' : 'hover:border-primary hover:bg-accent/40'
      }`}
    >
      <div className="flex w-full items-center justify-between">
        <Icon className="size-5 text-muted-foreground" aria-hidden />
        {added && (
          <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            <Check className="size-3.5" /> {t('taxonomy.addFieldModal.added')}
          </span>
        )}
      </div>
      <div className="text-sm font-medium text-foreground">{field.name}</div>
      <div className="truncate text-xs text-muted-foreground">{summary}</div>
    </button>
  );
}

/**
 * The shared list a card pulls from, if any.
 *
 * Two routes, because the two kinds of card identify their list differently:
 * a catalog entry maps its `key` through the standard-field→list table, while
 * an ORG template already stores a concrete `listRef.listId`. Resolving both
 * here keeps the card showing the REAL option count (250 countries, not the
 * handful the inline fallback carries).
 */
function boundListFor(
  field: PickerField,
  lists: TaxonomyListSummary[],
): TaxonomyListSummary | undefined {
  const listId = field.template?.listRef?.listId;
  if (listId) return lists.find((l) => l.id === listId);
  return field.template ? undefined : findStandardList(field.key, lists);
}

/** Short summary of a library field for its card. */
function fieldSummary(field: PickerField): string {
  if (field.type === 'date') {
    return i18n.t('knowledge:taxonomy.addFieldModal.dateWithFormat', {
      format: field.format ?? '',
    });
  }
  if (field.values.length === 0) return TAXONOMY_FIELD_TYPE_LABELS[field.type];
  const codes = field.values.slice(0, 4).map((x) => x.code);
  const more = field.values.length - codes.length;
  return `${codes.join(', ')}${more > 0 ? ` +${more}` : ''}`;
}

/**
 * Add field flow (SME / Trackers-style). Step 1 "pick": a category rail on the
 * left and icon cards on the right — either a standard library field (pre-filled
 * with values + codes) or a Custom field you define yourself. Step 2 "details":
 * name / section / type / format / required, pre-filled from the choice, before
 * adding. Contextual adds (subfield, add-to-right) skip the picker.
 */
export function AddFieldModal(props: AddFieldModalProps) {
  const { t } = useTranslation('knowledge');
  const {
    open,
    defaultGroup,
    existingFields,
    lockGroup,
    isSubfield,
    skipPicker,
    onClose,
    onAdd,
    orgWide,
  } = props;
  const nameId = useId();
  const formatId = useId();
  const maskListId = useId();
  const linkedSchemaFieldsOn = useBooleanFlagValue('taxonomy_linked_schema_fields', false);
  // Resolves a standard field's Standard List binding; cached across opens.
  const listsQuery = useQuery(taxonomyListsQueryOptions());
  // What a library field may bind. A workspace list cannot be read in this schema's other
  // workspaces and the server refuses it, so on an org-wide schema the field keeps its values.
  const bindableLists = useMemo(() => {
    const all = listsQuery.data ?? [];
    return orgWide ? all.filter((l) => l.scope === 'organization') : all;
  }, [listsQuery.data, orgWide]);
  // The org's own Fields library, merged into the picker alongside the
  // shipped catalog — see `buildCatalog`.
  const templatesQuery = useQuery(taxonomyFieldTemplatesQueryOptions());
  // Per-category icons chosen by the org; falls back to the shipped mapping.
  const categoriesQuery = useQuery(taxonomyCategoriesQueryOptions());
  const categoryIcons = useMemo(
    () => new Map((categoriesQuery.data ?? []).map((c) => [c.name, c.icon])),
    [categoriesQuery.data],
  );
  const catalog = useMemo(
    () => buildCatalog(templatesQuery.data ?? [], categoriesQuery.data ?? []),
    [templatesQuery.data, categoriesQuery.data],
  );

  const [step, setStep] = useState<'pick' | 'details'>(skipPicker ? 'details' : 'pick');
  const [activeCategory, setActiveCategory] = useState(
    STANDARD_FIELD_CATEGORIES[0]?.category ?? '',
  );

  const [name, setName] = useState('');
  const [group, setGroup] = useState<TaxonomyFieldGroup>(defaultGroup);
  const [type, setType] = useState<TaxonomyFieldType>('dropdown');
  const [required, setRequired] = useState(false);
  const [format, setFormat] = useState('');
  // eslint-disable-next-line i18next/no-literal-string -- the stored `level` value
  const [level, setLevel] = useState<string>('all');
  const binding = useListBinding({ open, type, orgWide });
  const maskExample = taxonomyDateMaskExample(format);

  useEffect(() => {
    if (!open) return;
    setStep(skipPicker ? 'details' : 'pick');
    setActiveCategory(STANDARD_FIELD_CATEGORIES[0]?.category ?? '');
    setName('');
    setGroup(defaultGroup);
    setType('dropdown');
    setRequired(false);
    setFormat('');
    // eslint-disable-next-line i18next/no-literal-string -- the stored `level` value
    setLevel('all');
  }, [open, defaultGroup, skipPicker]);

  const groupFor = (field: PickerField) => (lockGroup ? defaultGroup : field.group);

  const isAdded = (field: PickerField) =>
    existingFields.some(
      (f) =>
        f.group === groupFor(field) && f.name.trim().toLowerCase() === field.name.toLowerCase(),
    );

  // A library field already carries name/section/type/values — add it directly
  // (no details step) and keep the picker open so several can be pulled.
  //
  // When the org has seeded the matching Standard List, the column arrives BOUND
  // to it rather than owning a private copy of the vocabulary, so later fixes to
  // the list reach this schema. The inline values are still written: they are
  // what "Unlink from list" restores, and the fallback when no list is seeded.
  const addStandard = (field: PickerField) => {
    if (isAdded(field)) return;

    // An ORG template already carries its own governance config — level,
    // required/unique, validation pattern, transform and any list binding —
    // so copy those verbatim rather than re-deriving from a catalog key it
    // may not even have.
    if (field.template) {
      const t = field.template;
      // Unknown to the index (another workspace's list, a deleted one, or the index still loading)
      // is dropped too: the server refuses any list this schema cannot see.
      const keepsList = bindableLists.some((l) => l.id === t.listRef?.listId);
      onAdd({
        id: crypto.randomUUID(),
        name: t.name,
        group: lockGroup ? defaultGroup : t.group,
        type: t.type,
        required: t.required,
        unique: t.unique,
        // The three a reader authored rather than configured: the description IS
        // the data dictionary, and dropping it on the way in meant the library's
        // documentation never reached the schema that adopted the field.
        ...(t.description ? { description: t.description } : {}),
        visible: t.visible,
        ...(t.defaultValue ? { defaultValue: t.defaultValue } : {}),
        ...(t.format ? { format: t.format } : {}),
        ...(t.level ? { level: t.level } : {}),
        ...(t.validationPattern ? { validationPattern: t.validationPattern } : {}),
        ...(t.transform ? { transform: t.transform } : {}),
        ...(t.libraryKey ? { libraryKey: t.libraryKey } : {}),
        // Gated like the custom route below: a template can carry a binding
        // that predates the guard, or one the seeder matched by name, and
        // copying it onto a non-dropdown field spreads the bad state to every
        // schema that adds the field.
        ...(t.listRef && keepsList && canBindList(t.type) ? { listRef: t.listRef } : {}),
        values: t.values.map((v) => ({ ...v })),
      });
      return;
    }

    const listRef = standardFieldListRef(field.key, bindableLists);
    onAdd({
      id: crypto.randomUUID(),
      name: field.name,
      group: groupFor(field),
      type: field.type,
      required: false,
      ...(field.format ? { format: field.format } : {}),
      level: standardFieldLevel(field.key),
      libraryKey: field.key,
      // Gated like the two branches around it. Every shipped standard field
      // that maps to a list is a dropdown today, so this changes nothing —
      // but nothing pins that, and the predicate exists precisely because
      // several writers have to agree without depending on each other.
      ...(listRef && canBindList(field.type) ? { listRef } : {}),
      values: field.values.map((v) => ({ ...v })),
    });
  };

  const pickCustom = () => {
    setName('');
    setGroup(defaultGroup);
    setType('dropdown');
    setFormat('');
    setRequired(false);
    binding.setListId('');
    setStep('details');
  };

  // A Variable (computed) column is added empty and configured afterwards via its
  // column ⋮ menu → Configure. Close the picker so the new column is visible.
  const addVariable = () => {
    onAdd({
      id: crypto.randomUUID(),
      // eslint-disable-next-line i18next/no-literal-string -- persisted default name the user then renames
      name: 'Variable',
      group: defaultGroup,
      type: 'computed',
      required: false,
      pattern: [],
      values: [],
    });
    onClose();
  };

  // A Linked schema column is added empty and configured afterwards via its
  // column ⋮ menu → Configure (pick target schema + what to pull).
  const addLinked = () => {
    onAdd({
      id: crypto.randomUUID(),
      // eslint-disable-next-line i18next/no-literal-string -- persisted default name the user then renames
      name: 'Linked schema',
      group: defaultGroup,
      type: 'linked',
      required: false,
      link: { pullKind: 'tracking_code', includeInName: false },
      values: [],
    });
    onClose();
  };

  const trimmed = name.trim();
  // Between picking a list and its published columns arriving, Add would create a field with
  // neither a binding nor inline values.
  const canAdd = trimmed.length > 0 && !binding.blocksSave;

  const handleAdd = () => {
    if (!canAdd) return;
    const change = binding.change();
    const listRef = change.kind === 'bind' ? change.listRef : undefined;
    onAdd({
      id: crypto.randomUUID(),
      name: trimmed,
      group,
      type,
      required,
      ...(type === 'date' && format.trim() ? { format: format.trim() } : {}),
      level,
      ...(listRef ? { listRef } : {}),
      // A custom field starts with no inline vocabulary: its options come from
      // a bound list, or from the grid once the column exists.
      values: [],
    });
    onClose();
  };

  const title = isSubfield
    ? t('taxonomy.addFieldModal.addSubfield')
    : t('taxonomy.addFieldModal.addField');

  // ── Step 1: library picker ──────────────────────────────────────────────
  if (step === 'pick') {
    const category = catalog.find((c) => c.category === activeCategory);
    return (
      <FormSheet
        className="w-[44rem]"
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title={title}
      >
        <FormSheet.Body>
          <div className="flex h-[26rem] gap-4">
            {/* Category rail */}
            {/* w-48, not w-40: "Targeting & offer" was truncating at the old
                width, and org-defined categories can be longer still.
                Only the CATEGORY list scrolls — Special and Custom are pinned
                below it, so they stay reachable no matter how many categories
                the org adds. */}
            <nav className="flex w-48 shrink-0 flex-col border-r border-border-light pr-2">
              <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
                {/* Custom and Special have dedicated buttons below — skip them
                    here so an org template categorised "Custom" (the default in
                    the Fields tab) doesn't produce a second rail entry. */}
                {catalog
                  .filter(({ category: cat }) => cat !== CUSTOM && cat !== SPECIAL)
                  .map(({ category: cat }) => {
                    // An org-chosen icon wins; otherwise the shipped starter icon
                    // for this name, then the registry default.
                    const Icon = categoryIconFor(cat, categoryIcons.get(cat));
                    const active = cat === activeCategory;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setActiveCategory(cat)}
                        className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm font-medium transition-colors ${
                          active
                            ? 'bg-accent text-foreground'
                            : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
                        }`}
                      >
                        <Icon className="size-4 shrink-0" aria-hidden />
                        <span className="truncate">{cat}</span>
                      </button>
                    );
                  })}
              </div>

              {/* Pinned: always visible below the scrolling category list. */}
              <div className="mt-1 flex flex-col gap-1 border-t border-border-light pt-1">
                <button
                  type="button"
                  onClick={() => setActiveCategory(SPECIAL)}
                  className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm font-medium transition-colors ${
                    activeCategory === SPECIAL
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
                  }`}
                >
                  <Wand2 className="size-4 shrink-0" aria-hidden />
                  {SPECIAL}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveCategory(CUSTOM)}
                  className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm font-medium transition-colors ${
                    activeCategory === CUSTOM
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
                  }`}
                >
                  <SquarePen className="size-4 shrink-0" aria-hidden />
                  {CUSTOM}
                </button>
              </div>
            </nav>

            {/* Cards */}
            <div className="grid flex-1 auto-rows-min grid-cols-2 content-start gap-2.5 overflow-y-auto pr-1">
              {activeCategory === SPECIAL ? (
                <>
                  <button
                    type="button"
                    onClick={addVariable}
                    className="col-span-2 flex flex-col items-start gap-1.5 rounded-lg border border-dashed border-border-light bg-card p-4 text-left transition-colors hover:border-primary hover:bg-accent/40"
                  >
                    <Wand2 className="size-5 text-muted-foreground" aria-hidden />
                    <div className="text-sm font-medium text-foreground">
                      {t('taxonomy.addFieldModal.variable')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('taxonomy.addFieldModal.computedColumnAssembledOtherFields')}
                    </div>
                  </button>
                  {linkedSchemaFieldsOn && (
                    <button
                      type="button"
                      onClick={addLinked}
                      className="col-span-2 flex flex-col items-start gap-1.5 rounded-lg border border-dashed border-border-light bg-card p-4 text-left transition-colors hover:border-primary hover:bg-accent/40"
                    >
                      <Link2 className="size-5 text-muted-foreground" aria-hidden />
                      <div className="text-sm font-medium text-foreground">
                        {t('taxonomy.addFieldModal.linkedSchema')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('taxonomy.addFieldModal.pullsUniqueIdentifierAnotherSchema')}
                      </div>
                    </button>
                  )}
                </>
              ) : activeCategory === CUSTOM ? (
                <>
                  <button
                    type="button"
                    onClick={pickCustom}
                    className="col-span-2 flex flex-col items-start gap-1.5 rounded-lg border border-dashed border-border-light bg-card p-4 text-left transition-colors hover:border-primary hover:bg-accent/40"
                  >
                    <SquarePen className="size-5 text-muted-foreground" aria-hidden />
                    <div className="text-sm font-medium text-foreground">
                      {t('taxonomy.addFieldModal.customField')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('taxonomy.addFieldModal.defineYourOwnFieldName')}
                    </div>
                  </button>
                  {/* Fields the org saved to its library land here when they
                      kept the default "Custom" category. Without this they were
                      created successfully and then unreachable. */}
                  {category?.fields.map((field) => {
                    const boundList = boundListFor(field, bindableLists);
                    return (
                      <LibraryFieldCard
                        key={field.key}
                        field={field}
                        added={isAdded(field)}
                        summary={
                          boundList
                            ? t('taxonomy.addFieldModal.listWithOptions', {
                                name: boundList.name,
                                count: boundList.rowCount,
                              })
                            : fieldSummary(field)
                        }
                        onClick={() => addStandard(field)}
                      />
                    );
                  })}
                </>
              ) : category && category.fields.length === 0 ? (
                /* A category the org defined but hasn't put anything in yet. It
                   still belongs in the rail, so say why it's empty and point at
                   the one place that fills it rather than showing a blank pane. */
                <div className="col-span-2 flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-border-light px-6 py-10 text-center">
                  <p className="text-sm font-medium text-foreground">
                    {t('taxonomy.addFieldModal.noFieldsInCategoryYet', {
                      category: activeCategory,
                    })}
                  </p>
                  <p className="max-w-[24rem] text-xs text-muted-foreground">
                    {t('taxonomy.addFieldModal.addFieldCategoryFieldsTab')}
                  </p>
                </div>
              ) : (
                category?.fields.map((field) => {
                  const boundList = boundListFor(field, bindableLists);
                  return (
                    <LibraryFieldCard
                      key={field.key}
                      field={field}
                      added={isAdded(field)}
                      summary={
                        boundList
                          ? t('taxonomy.addFieldModal.listWithOptions', {
                              name: boundList.name,
                              count: boundList.rowCount,
                            })
                          : fieldSummary(field)
                      }
                      onClick={() => addStandard(field)}
                    />
                  );
                })
              )}
            </div>
          </div>
        </FormSheet.Body>

        <FormSheet.Footer primary={{ label: t('taxonomy.addFieldModal.done'), onClick: onClose }} />
      </FormSheet>
    );
  }

  // ── Step 2: details ───────────────────────────────────────────────────────
  return (
    <FormSheet
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={title}
      className="w-[min(calc(100vw-2rem),40rem)] sm:!max-w-[40rem]"
    >
      {/* Same sectioned layout as the Fields tab's New field dialog, so the two
          places you can define a field don't look like different products. */}
      <FormSheet.Body className="flex flex-col gap-5">
        <div className="flex flex-col gap-4">
          <SectionHeading>{t('taxonomy.addFieldModal.field')}</SectionHeading>

          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <FormField
              label={t('taxonomy.addFieldModal.fieldName')}
              htmlFor={nameId}
              required
              className="sm:col-span-2"
            >
              <Input
                id={nameId}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('taxonomy.addFieldModal.eGObjective')}
                maxLength={100}
                className="bg-input"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a modal the user just opened to type into
                autoFocus
              />
            </FormField>

            {!lockGroup && (
              <FormField
                label={t('taxonomy.addFieldModal.section')}
                // eslint-disable-next-line i18next-templates/no-literal-string -- element id, not copy
                labelId={`${nameId}-section`}
                hint={t('taxonomy.addFieldModal.whichHalfSchemaColumnBelongs')}
                className="sm:col-span-2"
              >
                <ToggleGroup
                  aria-labelledby={`${nameId}-section`}
                  type="single"
                  value={group}
                  onValueChange={(val) => val && setGroup(val as TaxonomyFieldGroup)}
                  variant="outline"
                  size="sm"
                  className="grid w-full grid-cols-2"
                >
                  {/* eslint-disable-next-line i18next/no-literal-string -- TaxonomyFieldGroup wire enum */}
                  <ToggleGroupItem value="media">
                    {t('taxonomy.addFieldModal.mediaField')}
                  </ToggleGroupItem>
                  {/* eslint-disable-next-line i18next/no-literal-string -- TaxonomyFieldGroup wire enum */}
                  <ToggleGroupItem value="content">
                    {t('taxonomy.addFieldModal.contentField')}
                  </ToggleGroupItem>
                </ToggleGroup>
              </FormField>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-border-light pt-5">
          <SectionHeading>{t('taxonomy.addFieldModal.dataType')}</SectionHeading>

          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <FormField label={t('taxonomy.addFieldModal.type')}>
              <Select
                value={type}
                onValueChange={(v) => {
                  const nextType = v as TaxonomyFieldType;
                  setType(nextType);
                  if (nextType !== 'date') setFormat('');
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAXONOMY_SELECTABLE_FIELD_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {TAXONOMY_FIELD_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <FormField
              label={t('taxonomy.addFieldModal.hierarchyLevel')}
              hint={t('taxonomy.addFieldModal.whereFieldSitsCampaignAd')}
            >
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAXONOMY_LEVEL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            {type === 'dropdown' && (
              <ListBindingFields binding={binding} className="sm:col-span-2" />
            )}

            {/* Date only — see the note in EditFieldModal. */}
            {type === 'date' && (
              <FormField
                label={t('taxonomy.addFieldModal.dateFormat')}
                htmlFor={formatId}
                className="sm:col-span-2"
                hint={
                  maskExample
                    ? t('taxonomy.addFieldModal.writesDatesAs', { example: maskExample })
                    : t('taxonomy.addFieldModal.leaveBlankForMask', { mask: DEFAULT_DATE_MASK })
                }
              >
                <Input
                  id={formatId}
                  list={maskListId}
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  placeholder={DEFAULT_DATE_MASK}
                  maxLength={40}
                  className="bg-input font-mono"
                />
                <datalist id={maskListId}>
                  {TAXONOMY_DATE_MASK_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.example}
                    </option>
                  ))}
                </datalist>
              </FormField>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-border-light pt-5">
          <SectionHeading>{t('taxonomy.addFieldModal.governance')}</SectionHeading>

          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col">
              <span className="text-sm font-medium">{t('taxonomy.addFieldModal.required')}</span>
              <span className="text-xs text-muted-foreground">
                {t('taxonomy.addFieldModal.generatedNamesMustIncludeValue')}
              </span>
            </div>
            <Switch
              checked={required}
              onCheckedChange={setRequired}
              aria-label={t('taxonomy.addFieldModal.requiredField')}
            />
          </div>
        </div>
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label: title,
          onClick: handleAdd,
          disabled: !canAdd,
          loading: binding.blocksSave && binding.detailPending,
        }}
        secondary={
          skipPicker
            ? { label: t('taxonomy.addFieldModal.cancel'), onClick: onClose }
            : { label: t('taxonomy.addFieldModal.back'), onClick: () => setStep('pick') }
        }
      />
    </FormSheet>
  );
}

AddFieldModal.displayName = 'AddFieldModal';
